import { Box, Center, Flex, Spinner, Text, useToast } from '@chakra-ui/react'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ChatHeader from '../components/ChatHeader'
import { ChatInterface } from '../components/ChatInterface/ChatInterface'
import { SidebarToggleButton } from '../components/Sidebar'
import ChatSidebar from '../components/Sidebar/ChatSidebar'

import { useAgents } from '../hooks/agents/useAgents'

import { useChat } from '../hooks/chat/useChat'
import { useChatLogic } from '../hooks/chat/useChatLogic'
import { Feature } from '../core/client/features.gen'
import { useFeature } from '../core/features/FeaturesProvider'
import { useModels } from '../hooks/models/useModels'
import { useSelectedModel } from '../hooks/models/useSelectedModel'
import { useLanguage } from '../hooks/utility/useLanguage'
import useMobile from '../hooks/utility/useMobile'
import { parseChatToMessageThread } from '../utils/threadUtil'
import { clearConversationContext, setConversationContext } from '../datadogConfig'
import { useSidebar } from '../context/SidebarContext'

const ChatPage = () => {
  const navigate = useNavigate()
  const toast = useToast()

  const isMobile = useMobile()
  const { isOpen: isSidebarOpen, toggle: handleSidebarToggle } = useSidebar()

  const { agentId } = useParams()
  const { chatId: currentChatId } = useParams()
  const queryClient = useQueryClient()
  const { t } = useLanguage()
  const showModelSelection = useFeature(Feature.MODEL_SELECTION)
  const { agents, currentAgent } = useAgents()
  const { models } = useModels()
  const { selectedModelId, selectModel } = useSelectedModel(currentAgent?.ai_model_id)

  // Use the chat details hook with the chat ID from URL
  const { chat: selectedChat, isLoading: isLoadingChatDetails } = useChat(currentChatId)

  // Callback for when a message is sent successfully to invalidate TanStack Query cache
  const handleMessageSentSuccessfully = useCallback(
    (messageId: string, currentChatId: string) => {
      queryClient.invalidateQueries({ queryKey: ['chat', currentChatId] })
    },
    [queryClient],
  )

  // Initialize the unified chat logic hook
  const {
    conversation,
    setConversation,
    clearConversation,
    clearConversationAndRegenerateChatId,
    isTyping,
    setIsTyping,
    isLoading: isStreaming,
    sendMessage,
    stopStreaming,
    handleRetry,
    toggleResponseMinimized,
    chatContainerRef,
    isInputDisabled: isHookInputDisabled,
    hasDocumentsInConversation,
    currentPhase,
    currentChatId: activeChatId,
  } = useChatLogic({
    existingChatId: currentChatId,
    onMessageSentSuccessfully: handleMessageSentSuccessfully,
    isNewChatSession: false,
    agentId: agentId,
    modelId: showModelSelection ? selectedModelId : undefined,
  })

  // Tag RUM events with the current conversation id so Datadog can filter
  // by chat even though chat IDs are scrubbed from URLs. Cleared on unmount
  // and when leaving a chat (homepage / new chat).
  useEffect(() => {
    if (currentChatId) {
      setConversationContext(currentChatId)
      return () => clearConversationContext()
    }
    clearConversationContext()
    return undefined
  }, [currentChatId])

  // Effect to update conversation when selectedChat changes
  useEffect(() => {
    if (selectedChat && selectedChat.messages && selectedChat.messages.length > 0) {
      const formattedThread = parseChatToMessageThread(selectedChat)
      setConversation(formattedThread)
    } else if (!currentChatId) {
      // Clear conversation when on homepage (no chatId)
      clearConversation()
    }
  }, [selectedChat, currentChatId, setConversation, clearConversation])

  // Sync agent from chat data with context (important for page refresh and legacy routes)
  useEffect(() => {
    if (selectedChat && selectedChat.agentId && agents.length > 0) {
      const chatAgent = agents.find(agent => agent.id === selectedChat.agentId)

      if (!chatAgent) {
        // Chat belongs to an agent that no longer exists
        toast({
          title: t('errors.agentNotFound'),
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
        navigate('/', { replace: true })
        return
      }

      // If we have an agent in the URL, check if it matches the chat's agent
      if (agentId && agentId !== selectedChat.agentId) {
        // URL agent doesn't match chat's agent - show error and redirect
        toast({
          title: t('errors.conversationNotFound'),
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top-right',
        })
        navigate(`/a/${agentId}`, { replace: true })
        return
      }

      // For legacy routes (/c/{chatId}), redirect to agent-specific route
      if (!agentId) {
        navigate(`/a/${selectedChat.agentId}/c/${currentChatId}`, { replace: true })
      }
    }
  }, [selectedChat, agents, agentId, currentChatId, navigate, toast, t])

  const handleSelectChat = (selectedChatId: string, chatAgentId?: string) => {
    if (selectedChatId !== currentChatId) {
      // Clear conversation when switching to a different chat
      clearConversation()

      if (chatAgentId) {
        // Navigate to agent-specific chat route
        navigate(`/a/${chatAgentId}/c/${selectedChatId}`)
      } else {
        // Fallback for legacy routes without agent
        navigate(`/c/${selectedChatId}`)
      }
    }
  }

  const handleSelectAgent = (selectedAgentId: string) => {
    if (selectedAgentId !== agentId) {
      clearConversationAndRegenerateChatId()

      navigate(`/a/${selectedAgentId}`)
    }
  }

  const handleNewChat = () => {
    clearConversationAndRegenerateChatId()

    // Navigate to agent-specific home (agentId is always available)
    navigate(`/a/${agentId}`)
  }

  // Overall input disabled state, considering initial chat details loading and hook's own disable state
  const isInputDisabled = isHookInputDisabled || isLoadingChatDetails

  return (
    <Flex width='100vw' height='100dvh' flexDirection='row' position='relative'>
      {/* Sidebar - Always render, but it slides out when closed */}
      <ChatSidebar
        onNewChat={handleNewChat}
        isLoadingDetails={isLoadingChatDetails}
        selectedConversationId={currentChatId}
        onSelectConversation={handleSelectChat}
        onSelectAgent={handleSelectAgent}
        isSidebarOpenExternal={isSidebarOpen}
        onSidebarToggle={handleSidebarToggle}
        onClearConversation={clearConversation}
      />

      <SidebarToggleButton isOpen={isSidebarOpen} onToggle={handleSidebarToggle} isFloating={true} />

      {/* Main content area */}
      <Flex
        flex='1'
        h='100dvh'
        flexDirection='column'
        minW='0'
        ml={isSidebarOpen && !isMobile ? '300px' : '0'}
        transition='margin-left 0.3s ease-in-out'
      >
        <ChatHeader isSidebarOpen={isSidebarOpen} />
        <Box flex='1' overflowY='hidden' minHeight='0'>
          {isLoadingChatDetails ? (
            <Center h='100%' flexDirection='column'>
              <Spinner thickness='4px' speed='0.65s' emptyColor='gray.200' color='primary' size='xl' mb={4} />
              <Box textAlign='center'>
                <Text fontSize='xl' fontWeight='medium' color='gray.600'>
                  {t('chat.loading')}
                </Text>
                {selectedChat?.title && (
                  <Text fontSize='md' color='gray.500' mt={2}>
                    {`"${selectedChat.title}"`}
                  </Text>
                )}
              </Box>
            </Center>
          ) : (
            <ChatInterface
              conversation={conversation}
              chatContainerRef={chatContainerRef}
              isInputDisabled={isInputDisabled}
              isTyping={isTyping}
              onSend={sendMessage}
              onRetry={handleRetry}
              onTypingChange={setIsTyping}
              hasDocumentsInConversation={hasDocumentsInConversation}
              onToggleResponseMinimized={toggleResponseMinimized}
              currentPhase={currentPhase}
              isStreaming={isStreaming}
              onStop={stopStreaming}
              models={showModelSelection ? models : undefined}
              selectedModelId={selectedModelId}
              onSelectModel={selectModel}
              conversationId={activeChatId}
            />
          )}
        </Box>
      </Flex>

      {/* Mobile overlay backdrop */}
      {isMobile && isSidebarOpen && (
        <Box
          position='fixed'
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg='blackAlpha.300'
          zIndex={999}
          onClick={handleSidebarToggle}
        />
      )}

    </Flex>
  )
}

export default ChatPage
